/**
 * @fileoverview Theme Sync Hook
 * 
 * Syncs the user's theme preference with the database so it persists
 * across sessions and devices. On login, the theme is restored from
 * the user's profile. When changed, it's saved to the database.
 * 
 * @module hooks/useThemeSync
 */

import { useEffect, useRef } from 'react';
import { useTheme } from 'next-themes';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Syncs theme preference with user profile in database.
 * 
 * Behavior:
 * - On login: Fetches theme from profile and applies it
 * - On theme change: Saves to profile (debounced to avoid rapid writes)
 * - Logged out: Falls back to localStorage (next-themes default)
 */
export function useThemeSync() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const isInitializedRef = useRef(false);
  const isSyncingRef = useRef(false);

  // Fetch and apply theme from database on login
  useEffect(() => {
    if (!user) {
      isInitializedRef.current = false;
      return;
    }

    // Avoid refetching if already initialized for this user
    if (isInitializedRef.current) return;

    const fetchTheme = async () => {
      try {
        isSyncingRef.current = true;
        const { data: profile } = await supabase
          .from('profiles')
          .select('theme')
          .eq('user_id', user.id)
          .single();

        // If user has an explicit theme preference (not default "system"), apply it
        if (profile?.theme && profile.theme !== 'system' && profile.theme !== theme) {
          setTheme(profile.theme);
        } 
        // If profile has "system" (default) but user has a specific theme in localStorage,
        // save their current preference to the database for consistency
        else if (profile?.theme === 'system' && theme && theme !== 'system') {
          await supabase
            .from('profiles')
            .update({ theme })
            .eq('user_id', user.id);
        }
        
        isInitializedRef.current = true;
      } catch (error) {
        // Profile might not exist yet, that's ok
      } finally {
        isSyncingRef.current = false;
      }
    };

    fetchTheme();
  }, [user, setTheme, theme]);

  // Save theme to database when it changes
  useEffect(() => {
    if (!user || !theme || !isInitializedRef.current || isSyncingRef.current) return;

    // Debounce to avoid rapid writes during initialization
    const timeoutId = setTimeout(async () => {
      try {
        await supabase
          .from('profiles')
          .update({ theme })
          .eq('user_id', user.id);
      } catch (error) {
        // Silently fail - localStorage will still work as fallback
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [user, theme]);
}
